// src/pages/admin/Register.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Container from "../../components/Container";
import api from "../../api/axios";

export default function AdminRegister() {
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [birthDate, setBirthDate] = useState(""); // YYYY-MM-DD
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const validate = () => {
    if (!email.trim()) return "이메일을 입력해주세요.";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email.trim()))
      return "유효한 이메일 형식을 입력해주세요.";

    if (!name.trim()) return "이름을 입력해주세요.";

    if (!password.trim()) return "비밀번호를 입력해주세요.";
    if (password.length < 8) return "비밀번호는 최소 8자 이상이어야 합니다.";

    if (!passwordConfirm.trim()) return "비밀번호 확인을 입력해주세요.";
    if (password !== passwordConfirm)
      return "비밀번호와 비밀번호 확인이 일치하지 않습니다.";

    if (!birthDate) return "생년월일을 선택해주세요.";

    // 주소/전화번호는 옵션이지만 DTO에 maxLength 제한 있으니 너무 길지 않게만 체크(필요하면 추가)
    if (address.length > 255)
      return "주소는 최대 255자까지 입력할 수 있습니다.";
    if (phone.length > 20)
      return "전화번호는 최대 20자까지 입력할 수 있습니다.";

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setDone(false);

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: email.trim(),
        name: name.trim(),
        password: password,
        userType: "ADMIN", // 🔒 관리자 계정 고정
        birthDate, // "YYYY-MM-DD"
        address: address.trim() || "",
        phone: phone.trim() || "",
      };

      const res = await api.post("/api/user/register", payload, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("register res", res.data);
      setDone(true);

      // 성공 시 로그인 페이지로 이동
      setTimeout(() => {
        nav("/login", { replace: true });
      }, 800);
    } catch (err) {
      console.error(err);
      const msgFromServer =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "관리자 계정 등록 중 오류가 발생했습니다.";
      setError(msgFromServer);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Container className="flex min-h-screen items-center justify-center py-10">
        <div className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-blue-900">관리자 계정 등록</h1>
          <p className="mt-1 text-sm text-slate-600">
            인피니티 관리자 페이지 접속을 위한 계정을 등록합니다.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {/* 이메일 */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                이메일 <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 이름 */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                이름 <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="관리자 이름"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 비밀번호 */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                비밀번호 <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="최소 8자 이상"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 비밀번호 확인 */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                비밀번호 확인 <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호를 한 번 더 입력"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 생년월일 */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                생년월일 <span className="text-red-600">*</span>
              </label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 주소 (선택) */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                주소{" "}
                <span className="text-xs font-normal text-slate-400">
                  (선택)
                </span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="서울시 ..."
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 전화번호 (선택) */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                전화번호{" "}
                <span className="text-xs font-normal text-slate-400">
                  (선택)
                </span>
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-0000-0000"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 메시지 영역 */}
            {error && (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
            {done && (
              <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                관리자 계정이 성공적으로 등록되었습니다. 잠시 후 로그인 페이지로
                이동합니다.
              </div>
            )}

            {/* 버튼 */}
            <div className="mt-4 flex items-center justify-between">
              <Link
                to="/admin/login"
                className="text-xs text-slate-500 hover:underline"
              >
                이미 계정이 있으신가요? 로그인
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? "등록 중..." : "관리자 등록"}
              </button>
            </div>
          </form>
        </div>
      </Container>
    </div>
  );
}
